import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import { SHAPEFILE_FIELDS } from "constants/settings.constants";
import { RootState } from "store/rootReducer";
import { parseShapefileProperty } from "utils/column/parseShapefileProperty";

import { IGroupByListColumn } from "models";
import { isShapefileBoundaryProperty } from "models/groupBy";
import { IShapefileNode } from "models/projects";

// Imported from the hook's own module (not the context barrel): the barrel
// re-exports ProjectContext.tsx, whose provider drags ProjectWrapper and a
// large slice of the app into the module graph of every consumer.
import { useProjectContext } from "components/project/projects/context/useProjectContext";

import { useProjectShapefilesQuery } from "../queries";

const collectShapefiles = (
  node: IShapefileNode,
  titles: Map<string, string>,
  ids: Set<string>
): void => {
  if (node?.shapefileId) {
    // Presence and name are tracked separately on purpose: a titleless node is
    // still a shapefile that exists, and using the title map as the membership
    // test would report it as deleted - silently dropping a working field.
    ids.add(node.shapefileId);
    if (node.title) titles.set(node.shapefileId, node.title);
  }
  node?.children?.forEach((child) => collectShapefiles(child, titles, ids));
};

/**
 * The shapefile tree for the project whose columns are currently being read,
 * flattened to `titleById` for naming and `idsInTree` for existence.
 *
 * `hasTree` is for callers that need to tell "this shapefile is gone" from "no
 * tree to check it against". It requires a body as well as a successful status,
 * since a 200 carrying nothing would otherwise read as an authoritative empty
 * tree. `hasTree` alone is NOT proof a real project was consulted: the query is
 * enabled whenever projectId !== undefined, and before project initialization
 * the Redux default is null - project-svc answers /projectTree/null with a 200
 * and three empty roots. Callers judging absence must also require `projectId`.
 * An empty `idsInTree` on its own proves nothing either way - it is both the
 * pre-load default and the legitimate state of a project whose last shapefile
 * was just deleted, which is exactly the case callers are trying to detect.
 */
const useShapefileTitles = () => {
  const projectId = useSelector(
    (state: RootState) => state.groupBy.selectedProjectIdForColumns
  );
  // The group-by Project Fields dropdown can point projectId at a project
  // other than the selected one - intended for *browsing* that project's
  // fields and shapefiles, and nothing resets it when group-by closes. The
  // judgment hooks below must not treat the browsed project's tree as
  // authority over what the selected project's map shows, so they require the
  // two to agree. Outside the project provider the context falls back to its
  // initial state (selectedProjectId undefined), which reads as "unknown" and
  // does not block - only a known divergence does.
  const { selectedProjectId } = useProjectContext();
  const isTreeForSelectedProject =
    selectedProjectId == null || selectedProjectId === projectId;

  const { data, isSuccess } = useProjectShapefilesQuery({ projectId });

  const { titleById, idsInTree } = useMemo(() => {
    const titles = new Map<string, string>();
    const ids = new Set<string>();
    [data?.McDaniel, data?.Organization, data?.Project].forEach(
      (root) => root && collectShapefiles(root, titles, ids)
    );
    return { titleById: titles, idsInTree: ids };
  }, [data]);

  return {
    projectId,
    titleById,
    idsInTree,
    hasTree: isSuccess && !!data,
    isTreeForSelectedProject
  };
};

/**
 * Resolves a persisted shapefile field key (`Shapefile_{projectId}_
 * {shapefileId}_{btoa(column)}`) to a synthetic column so the shared label
 * logic renders "Shapefile Name: Field". Needed because shapefile columns are
 * only materialized into the live column list when a user picks the shapefile
 * in the group-by tree (ShapefileCategory) — on reload from user defaults they
 * aren't present, so the tooltip/settings would otherwise show the raw id.
 */
export const useResolveShapefileColumn = (): ((
  property: string
) => Nullable<IGroupByListColumn>) => {
  const { titleById } = useShapefileTitles();

  return useCallback(
    (property: string): Nullable<IGroupByListColumn> => {
      // The boundary key's column name is a reserved sentinel, never a
      // display string - same guard as useLoadShapefileColumn, so a stale
      // key persisted by an older session resolves to nothing rather than
      // to a column named after the sentinel.
      if (isShapefileBoundaryProperty(property)) return null;
      const parsed = parseShapefileProperty(property);
      if (!parsed) return null;
      const shapefileTitle = titleById.get(parsed.shapefileId);
      if (!shapefileTitle) return null;
      return {
        property,
        title: parsed.columnName,
        subgroup: shapefileTitle,
        group: SHAPEFILE_FIELDS
      } as IGroupByListColumn;
    },
    [titleById]
  );
};

/**
 * Last-resort synthetic column for a shapefile field key that no tree can name,
 * built from the key itself - the column name is carried in the key, base64
 * encoded (buildShapefileProperty).
 *
 * Without this, an unnameable key falls through getColumnName's
 * `if (!column) return propertyName` and the raw
 * `Shapefile_{projectId}_{shapefileId}_{base64}` string is what the user reads
 * (EVA-126). The shapefile's own name is genuinely unavailable here, so this
 * deliberately carries no `group` or `subgroup`: getFieldContextPrefix then
 * yields nothing and the label is the bare field name ("County") rather than a
 * misleading prefix.
 */
export const shapefileColumnFromKey = (
  property: string
): Nullable<IGroupByListColumn> => {
  // The boundary key's column name is a reserved sentinel, never a display
  // string - naming a row from it would render the raw sentinel (see
  // withoutShapefileBoundaryColumns).
  if (isShapefileBoundaryProperty(property)) return null;
  const parsed = parseShapefileProperty(property);
  if (!parsed) return null;
  return { property, title: parsed.columnName } as IGroupByListColumn;
};

/**
 * Whether a field key's shapefile is *positively* gone from the selected
 * project, as opposed to merely unresolvable right now (EVA-126). "Gone" here
 * means absent from the project's shapefile tree: deleted outright, or an
 * org/McDaniel shapefile that was unlinked from the project. Both leave the
 * key behind in the persisted tooltip/focus lists - those store raw property
 * strings - and an unresolvable key still gets sent to the well-detail
 * endpoint and rendered with the raw key as its label, via getColumnName's
 * `if (!column) return propertyName` fallback.
 *
 * Treating unlink like deletion is deliberate, and is NOT a disagreement with
 * usePurgeDeletedShapefilesFromDefaults staying out of the unlink paths: this
 * guard only governs what the current project can show (an unlinked
 * shapefile's field cannot be resolved or named here either way), while the
 * purge is permanent destruction of the saved entry. Because unlink purges
 * nothing, re-linking the shapefile brings the field straight back to life -
 * and the settings row this guard produces only *offers* removal, it never
 * forces it.
 *
 * Deliberately conservative, for the same reason useResolvedDefaultFieldList
 * stays read-only: "can't resolve" has innocent causes, and treating one as a
 * deletion silently drops a field the user legitimately configured. Answers
 * false unless every one of these holds:
 *   - the key parses as a shapefile field;
 *   - a shapefile tree actually arrived - not idle (no project selected yet),
 *     loading, errored, or a success carrying no body (see useShapefileTitles);
 *   - the key was built while reading the project being read now. A key carries
 *     the project the shapefile was picked in (buildShapefileProperty), and an
 *     org-level shapefile picked in another project is absent from this tree
 *     without having been deleted;
 *   - the shapefile id is absent from that tree.
 *
 * Because of the project check this is a net, not a guarantee: a field stamped
 * with another project is never judged. Deletion is purged at source too - see
 * usePurgeDeletedShapefilesFromDefaults - so nothing depends on this catching
 * every case.
 */
export const useIsDeletedShapefileField = (): ((property: string) => boolean) => {
  const { projectId, idsInTree, hasTree, isTreeForSelectedProject } =
    useShapefileTitles();

  return useCallback(
    (property: string): boolean => {
      if (!hasTree || !isTreeForSelectedProject) return false;
      const parsed = parseShapefileProperty(property);
      if (!parsed) return false;
      if (parsed.projectId !== projectId) return false;
      return !idsInTree.has(parsed.shapefileId);
    },
    [projectId, idsInTree, hasTree, isTreeForSelectedProject]
  );
};

/**
 * Whether a field key's shapefile is absent from the selected project's loaded
 * tree - the display-time filter for the map tooltip (EVA-126). The saved
 * tooltip/focus lists are global user settings, but a shapefile field is only
 * meaningful in a project whose tree holds that shapefile: elsewhere it can
 * never resolve to a name and the well-detail endpoint has no polygon context
 * for it, so it rendered as a junk row ("Field: n/a") in every other project.
 *
 * Differs from useIsDeletedShapefileField in exactly one way: no project-stamp
 * check. The stamp records where the shapefile was *picked*, not where it is
 * usable - an org/McDaniel shapefile picked in project A is stamped A yet
 * legitimately present in project B's tree, and membership is what's tested,
 * so such a field keeps working everywhere the shapefile is linked. The
 * deleted check keeps its stamp guard because it decides the settings row's
 * wording ("no longer in this project" vs "belongs to another project") -
 * either way removal is offered, never performed, and switching back to the
 * field's project brings a hidden field straight back.
 *
 * Hiding is deliberate even though the well-detail service could sometimes
 * answer: it resolves a shapefile field by the project stamped in the key, so
 * where two projects overlap geographically a foreign field can carry a real
 * value. Product call on EVA-126: shapefile fields are scoped to projects
 * whose tree holds the shapefile, so that value is not shown here.
 *
 * Shares the deleted check's conservatism about evidence: answers false while
 * no tree has arrived (idle, loading, errored, or a bodyless success), and for
 * keys that don't parse as shapefile fields. It also requires a selected
 * project: before project initialization the tree endpoint is queried with a
 * null id and answers 200 with empty roots, which would otherwise read as
 * authority to condemn every shapefile field (see useShapefileTitles).
 *
 * The group-by Project Fields dropdown can point selectedProjectIdForColumns
 * at a project other than the selected one - by design, for browsing that
 * project's fields. While the two diverge this declines to judge (see
 * useShapefileTitles), so browsing another project's fields never changes
 * what the selected project's map shows. The settings pane mounts its own
 * bare ProjectProvider, so for its first render(s) the selected project reads
 * as unknown and the guard is permissive there until useProjectInitialization
 * syncs both ids - a brief window, and strictly narrower than having no guard.
 * The dropdown leaving the pointer diverged after group-by closes - which
 * also skews column naming - predates this hook and is being addressed
 * separately as its own piece of work.
 */
export const useIsShapefileFieldAbsentFromProject = (): ((
  property: string
) => boolean) => {
  const { projectId, idsInTree, hasTree, isTreeForSelectedProject } =
    useShapefileTitles();

  return useCallback(
    (property: string): boolean => {
      if (!projectId || !hasTree || !isTreeForSelectedProject) return false;
      const parsed = parseShapefileProperty(property);
      if (!parsed) return false;
      return !idsInTree.has(parsed.shapefileId);
    },
    [projectId, idsInTree, hasTree, isTreeForSelectedProject]
  );
};
